import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import colors from "../../utils/colors";

const INDIGO = colors.primary[300];
const BORDER = colors.neutral[200];
const FG1 = colors.neutral[1000];
const FG2 = colors.neutral[700];

export default function StickySaveBar({
  open,
  modifiedCount,
  modifiedFields,
  onDiscard,
  onApply,
  isApplying = false,
}) {
  if (!open) return null;

  const count = typeof modifiedCount === "number" ? modifiedCount : 0;
  const fields = Array.isArray(modifiedFields) ? modifiedFields.filter(Boolean) : [];
  const showFieldTooltip = fields.length > 0;

  const countLabel = (
    <Typography
      component="span"
      sx={{
        fontSize: 11.5,
        color: FG2,
        cursor: showFieldTooltip ? "help" : "default",
        textDecoration: showFieldTooltip ? "underline dotted" : "none",
        textUnderlineOffset: "2px",
      }}
    >
      {count} field{count === 1 ? "" : "s"} modified
    </Typography>
  );

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 18,
        left: "50%",
        transform: "translateX(-50%)",
        background: "#EDF3FE",
        color: FG1,
        borderRadius: "14px",
        border: `1px solid ${INDIGO}`,
        padding: "10px 12px 10px 18px",
        display: "flex",
        alignItems: "center",
        gap: 2,
        boxShadow: "0 12px 40px rgba(99,113,247,0.18)",
        zIndex: 30,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: INDIGO,
          animation: "bmconfig-pulse 1.6s ease-in-out infinite",
          "@keyframes bmconfig-pulse": {
            "0%,100%": { opacity: 0.6 },
            "50%": { opacity: 1 },
          },
        }}
      />
      <Typography
        sx={ { fontSize: 13, fontWeight: 500, color: FG1 }}
      >
        Unsaved changes
      </Typography>
      {showFieldTooltip ? (
        <Tooltip
          arrow
          placement="top"
          title={(
            <Box component="ul" sx={{ m: 0, pl: 2.25, py: 0.25 }}>
              {fields.map((field) => (
                <Box
                  component="li"
                  key={field}
                  sx={{ fontSize: 12, lineHeight: "18px", "&:not(:last-child)": { mb: 0.5 } }}
                >
                  {field}
                </Box>
              ))}
            </Box>
          )}
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 360,
                fontSize: 12,
                lineHeight: "18px",
                p: 1.25,
                backgroundColor: "#1F2937",
                "& .MuiTooltip-arrow": { color: "#1F2937" },
              },
            },
          }}
        >
          {countLabel}
        </Tooltip>
      ) : countLabel}
      <Box
        onClick={isApplying ? undefined : onDiscard}
        sx={{
          background: "transparent",
          color: FG1,
          border: `1px solid ${BORDER}`,
          padding: "6px 14px",
          borderRadius: "8px",
          cursor: isApplying ? "not-allowed" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: 13,
          fontWeight: 500,
          opacity: isApplying ? 0.7 : 1,
          "&:hover": isApplying
            ? undefined
            : { background: "rgba(99,113,247,0.06)", borderColor: INDIGO },
        }}
      >
        Discard
      </Box>
      <Box
        onClick={isApplying ? undefined : onApply}
        sx={{
          background: INDIGO,
          color: "#fff",
          padding: "6px 14px",
          borderRadius: "8px",
          cursor: isApplying ? "wait" : "pointer",
          opacity: isApplying ? 0.7 : 1,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: 13,
          fontWeight: 500,
          "&:hover": { background: colors.primary[400] },
        }}
      >
        <CheckOutlinedIcon sx={{ width: 14, height: 14 }} />
        Apply changes
      </Box>
    </Box>
  );
}

